<template>
    <page-layout :readonly="isProposalAccepted" :hide-footer="isProposalAccepted">
        <template #header>
            <proposal-step-header
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :use-exit-modal="false"
                title="Social Networks"
            />
        </template>
        <template #default>
            <g-loader v-if="isLoading" type="fixed" />
            <div v-else-if="showSharedNetworks">
                <shared-networks
                    :network-id="sharedNetworks.networkId"
                    :social-network-id="sharedNetworks.socialNetworkId"
                    @back="handleCloseSharedNetworks"
                    @success="handleShareNetworkSuccess"
                    @proceed-to-connect="proceedToConnect"
                    @popup-closed="handlePopupClosed"
                    @clear-proceed-to-connect="clearProceedToConnect"
                />
            </div>
            <template v-else>
                <div v-if="customNetworksStepOverview" class="margin-bottom-large">
                    <g-typography tag="p" :value="customNetworksStepOverview" class="break-new-line" />
                </div>
                <div v-else class="margin-bottom-large">
                    <div class="margin-bottom-small">
                        <g-typography tag="p">
                            Connecting your accounts allows us to reliably pull in content you create during campaigns,
                            so you don't have to manually upload your deliverables.
                        </g-typography>
                    </div>
                    <g-typography tag="p">
                        This <b>does not</b> give us access to post content or to access your private messages.
                    </g-typography>
                </div>

                <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                <proposal-step-error
                    v-if="proposalStep && proposalStep.error"
                    class="margin-bottom-normal"
                    :step="proposalStep"
                />

                <div v-if="requiredNetworks.length > 0" class="margin-bottom-large">
                    <div class="margin-bottom-normal">
                        <g-typography tag="span" font-weight="semibold">Required</g-typography>
                    </div>
                    <network-manager
                        :contact-id="contact.id"
                        context="proposal"
                        :networks="requiredNetworks"
                        :contact-networks="contact.networks"
                        :tasks="proposal.tasks"
                        :readonly="isProposalAccepted"
                        :proceed-to-connect-campaigns-prop="proceedToConnectProp"
                        @load-contact="forceReloadContact"
                        @close-shared-networks="handleCloseSharedNetworks"
                        @show-shared-networks="handleShowSharedNetworks"
                    />
                </div>

                <div v-if="optionalNetworks.length > 0" class="margin-bottom-normal">
                    <div class="margin-bottom-normal">
                        <g-typography tag="span" font-weight="semibold">Optional</g-typography>
                    </div>
                    <network-manager
                        :contact-id="contact.id"
                        context="proposal"
                        :networks="optionalNetworks"
                        :contact-networks="contact.networks"
                        :tasks="proposal.tasks"
                        :readonly="isProposalAccepted"
                        :proceed-to-connect-campaigns-prop="proceedToConnectProp"
                        @load-contact="forceReloadContact"
                        @close-shared-networks="handleCloseSharedNetworks"
                        @show-shared-networks="handleShowSharedNetworks"
                    />
                </div>
            </template>
        </template>
        <template #footer>
            <proposal-step-footer
                :is-next-disabled="isNextDisabled"
                :loading="isCompletingProposal"
                :use-exit-modal="false"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                @go-next-step="$emit('go-next-step', proposalStep)"
                @go-previous-step="$emit('go-previous-step', proposalStep)"
            />
        </template>
    </page-layout>
</template>

<script>
    import PageLayout from '../../layouts/PageLayout.vue';
    import ProposalStepFooter from './partials/ProposalStepFooter.vue';
    import ProposalStepHeader from './partials/ProposalStepHeader.vue';
    import * as _ from 'lodash';
    import { getMeta } from '../../../../src/helpers';
    import NetworkConnectionMixin from '../../../mixins/NetworkConnectionMixin';
    import ErrorMessageCard from './partials/ErrorMessageCard.vue';
    import ProposalStepError from './partials/PoposalStepError.vue';
    import { LIVE_CAMPAIGN_PROPOSAL_NETWORK_CONNECTION_EVENTS } from '../../../constants/analytics-events';
    import ProposalMixin from './mixins/ProposalMixin';
    import { INSTAGRAM_ID, TIKTOK_ID, PINTEREST_ID, YOUTUBE_ID, FACEBOOK_ID } from '../../../constants/networks';
    import NetworkManager from '../networks/partials/NetworkManager.vue';
    import SharedNetworks from '../../../../app-v2/components/data-views/sharedNetworks/SharedNetworks.vue';
    import { useCreatorStore, useFeatureFlagsStore } from '../../../../stores';
    import useNetworks from '../../../../src/composables/use-networks';

    /**
     * These networks only require a soft connection
     */
    const ALLOW_SOFT_CONNECTION_NETWORK_IDS = [YOUTUBE_ID, TIKTOK_ID, PINTEREST_ID];

    export default {
        name: 'ProposalNetworksView',
        components: {
            NetworkManager,
            ProposalStepError,
            ErrorMessageCard,
            PageLayout,
            ProposalStepHeader,
            ProposalStepFooter,
            SharedNetworks
        },
        props: {
            campaign: {
                type: Object,
                default: () => ({})
            },
            proposal: {
                type: Object,
                default: () => ({})
            },
            contact: {
                type: Object,
                default: () => ({})
            },
            proposalStep: {
                type: Object,
                default: () => ({})
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingProposal: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                default: null
            },
            forceReloadContact: {
                type: Function
            },
            forceReloadProposal: {
                type: Function
            }
        },
        mixins: [NetworkConnectionMixin, ProposalMixin],
        data() {
            const creatorStore = useCreatorStore();
            return {
                showSharedNetworks: false,
                sharedNetworks: {
                    networkId: null,
                    socialNetworkId: null
                },
                creatorStore,
                proceedToConnectProp: {
                    value: false,
                    networkId: 0
                }
            };
        },
        computed: {
            host() {
                return getMeta('routing-domain');
            },
            stepNumber() {
                return _.get(this, 'proposalStep.meta.stepNumber', 0);
            },
            totalSteps() {
                return _.get(this, 'proposalStep.meta.totalSteps', 0);
            },
            isNextDisabled() {
                return !!this.requiredNetworks.find((requiredNetwork) => !this.isConnected(requiredNetwork));
            },
            optionalNetworks() {
                if (!this.campaign) {
                    return [];
                }

                const networks = this.campaign.tasks.filter((task) => task.network_id).map((task) => task.network);

                return _.uniqBy(networks, 'id') //removing duplicates
                    .filter((network) => {
                        //removing networks if it's already shown in required section
                        return !this.requiredNetworks.find((requiredNetwork) => requiredNetwork.id === network.id);
                    });
            },
            requiredNetworks() {
                if (!this.proposal) {
                    return [];
                }

                return _.uniqBy(
                    this.proposal.tasks.filter((task) => task.network_id && task.required).map((task) => task.network),
                    'id'
                );
            },
            customNetworksStepOverview() {
                return _.get(this.campaign, `customization.connect_networks_overview`, null);
            },
            isCompleted() {
                return _.get(this, 'proposalStep.is_complete', false);
            },
            useThirdParty() {
                return true;
            }
        },
        methods: {
            isConnected(network) {
                if (this.useThirdParty) {
                    if (network.id === FACEBOOK_ID) {
                        // TODO: Spacejam - Don't require auth for Facebook for TS-859
                        // Remove this when Facebook auth is available
                        return true;
                    }

                    if (network.id === INSTAGRAM_ID) {
                        // TODO: Spacejam - Don't require auth for Instagram for TS-875
                        // Remove this when 3rd party auth is available
                        return true;
                    }

                    return !!useNetworks().getConnectedContactNetworkByIdFromContactNetworks(
                        network.id,
                        this.contact.networks
                    );
                }

                return !!this.getContactNetwork(network);
            },

            getContactNetwork(network) {
                // if TikTok, Youtube or Pinterest is the network then we consider it connected if it exists
                // Instagram and graph is not required, we only need the soft connection
                if (
                    ALLOW_SOFT_CONNECTION_NETWORK_IDS.includes(network.id) ||
                    (network.id === INSTAGRAM_ID && !this.instagramGraphRequired)
                ) {
                    return this.contact.networks.find((contactNetwork) => contactNetwork.network_id === network.id);
                }

                // if it's any other network we must have an oauth connection
                return this.contact.networks.find((contactNetwork) => {
                    return (
                        contactNetwork.network_id === network.id && contactNetwork.network_connection_type === 'oauth'
                    );
                });
            },

            async reloadSharedNetworks() {
                await this.creatorStore.getAllLiveUrls();
                await this.creatorStore.getAllSocialNetworks();
            },

            async handlePopupClosed(networkId) {
                await this.forceReloadContact();
                await this.forceReloadProposal();
                await this.reloadSharedNetworks();

                const socialNetwork = this.creatorStore.getCurrentLiveUrlSocialNetwork(networkId);
                if (useNetworks().showSharedNetworkPostConnect(socialNetwork, this.$root.networks)) {
                    this.sharedNetworks.socialNetworkId = socialNetwork.id;
                    this.sharedNetworks.networkId = networkId;
                    this.showSharedNetworks = true;
                }
            },

            handleShowSharedNetworks(networkId, socialNetworkId) {
                this.sharedNetworks.networkId = networkId;
                this.sharedNetworks.socialNetworkId = socialNetworkId;
                this.showSharedNetworks = true;
            },

            handleCloseSharedNetworks() {
                this.showSharedNetworks = false;
            },

            handleShareNetworkSuccess() {
                this.handleCloseSharedNetworks();
                this.forceReloadContact();
                this.forceReloadProposal();
                this.reloadSharedNetworks();
            },
            proceedToConnect(networkId) {
                this.showSharedNetworks = false;
                this.proceedToConnectProp = {
                    value: true,
                    networkId
                };
            },
            clearProceedToConnect() {
                this.proceedToConnectProp = {
                    value: false,
                    networkId: 0
                };
            }
        },
        watch: {
            contact(newContact, oldContact) {
                if (newContact && oldContact) {
                    const newNetworks = _.differenceBy(
                        newContact.networks,
                        oldContact.networks,
                        (network) => network.network_id
                    );

                    newNetworks.forEach((network) => {
                        this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_NETWORK_CONNECTION_EVENTS[network.network.slug].success);
                    });
                }
            }
        },
        mounted() {
            const connectedNetworkId = this.$route?.query?.connectedNetworkId;
            if (connectedNetworkId) {
                const query = Object.assign({}, this.$route?.query);
                delete query.connectedNetworkId;
                this.$router?.replace({ query });
                this.handlePopupClosed(+connectedNetworkId);
            } else {
                this.forceReloadContact();
                this.forceReloadProposal();
                this.reloadSharedNetworks();
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .break-new-line {
        white-space: pre-line;
    }
</style>
